import { useGetDocuments, useGetMcpConnections } from "@hooks";
import type { GroupedToolCall } from "../group-tool-messages";
import { isHumanInTheLoopRejection } from "../human-in-the-loop";
import type { ChatToolMessage } from "../types";
import { useChatDeviceContext } from "../useChatDeviceContext";
import {
  getToolRowLabel,
  getToolServerName,
  getToolTarget,
  parseToolArgs,
  TOOL_PRESENTATION,
} from "./tool-call-presentation";
import { getToolIntegrationIcon } from "./utils/tool-calls";

export const useToolCallPresentation = (
  call: GroupedToolCall,
  result: ChatToolMessage | undefined,
  running: boolean,
  decision?: "approve" | "reject" | "edit"
) => {
  const { orgId, deviceId } = useChatDeviceContext();
  const { data: documents } = useGetDocuments(orgId, deviceId);
  const { data: connections } = useGetMcpConnections(orgId, !!orgId);
  const name = call.toolCall.name ?? "";
  const args = parseToolArgs(call.toolCall.args);
  const documentTool = [
    "read_document",
    "read_document_metadata",
    "edit_document",
    "edit_custom_document_text",
  ].includes(name);
  const inputTarget = getToolTarget(name, args);
  const pathId = inputTarget.split("/").filter(Boolean).at(-1) ?? inputTarget;
  const document = documentTool
    ? documents?.find(
        (doc) =>
          doc.id === inputTarget ||
          doc.id === pathId ||
          doc.tiptapPath === inputTarget
      )
    : undefined;
  const target = document?.title ?? inputTarget;
  const rejected = decision === "reject" || isHumanInTheLoopRejection(result);
  const failed = result?.status === "error" && !rejected;
  const active = running && !result;
  const approved = decision === "approve";
  const connectionNames =
    connections?.connections.map((connection) => connection.name) ?? [];
  const integrationIcon = TOOL_PRESENTATION[name]
    ? null
    : getToolIntegrationIcon(name, connections?.connections);
  const label = getToolRowLabel(
    name,
    {
      ...args,
      ...(document && documentTool
        ? {
            [name === "read_document" || name === "read_document_metadata"
              ? "id"
              : "document_id"]: document.title,
          }
        : {}),
    },
    active,
    approved || (!!result && !rejected && !failed),
    connectionNames
  );
  const labelWithoutTarget =
    target && label.endsWith(target) ? label.slice(0, -target.length) : label;
  const serverName = getToolServerName(name, connectionNames);
  return {
    deviceId,
    connections,
    name,
    document,
    documentTool,
    inputTarget,
    label,
    labelWithoutTarget,
    serverName,
    integrationIcon,
    active,
    failed,
    approved,
    rejected,
  };
};
